import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core, Decorative } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Storage } from "@faire/web--source/common/Storage";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import * as React from "react";
import { useEffect } from "react";
import styled from "styled-components";
import { selectBadge } from "@faire/retailer/components/ProductBadge/badgeUtils";
import { ENABLE_CONSUMER_MODE } from "@faire/retailer/lib/consts";
import { isLoggedInRetailerOrBrandPreview } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { isProductOptionInStock } from "@faire/retailer/lib/productOptions";
import { showProductPromotions } from "@faire/retailer/lib/showProductPromotions";
import { useAssignSettingUpdateSaleColorCopy, useSettingUpdateSaleColorCopy } from "@faire/retailer/settings/useSettingUpdateSaleColorCopy";
import { useRetailerStore } from "@faire/retailer-visitor-shared/stores/domain/RetailerStore";
import { Price } from "@faire/retailer/views/product/ProductDetails/Price/Price";
import { PRICE_TEXT_SURFACE } from "@faire/retailer/views/product/ProductDetails/Price/PriceProps";
import { MaybeSimpleBrandTitle } from "@faire/retailer/views/product/ProductDetails/SimpleBrandTitle";
import { LoggedOutPrice } from "./__internal__/Header/LoggedOutPrice";
import { ProductDetailsPromotion } from "./__internal__/Header/ProductDetailsPromotion";
import { ProductName } from "./__internal__/Header/ProductName";
import { RedesignedOutOfStockText } from "./__internal__/Header/RedesignedOutOfStockText";
import { originalPriceMoney } from "./productOriginalPrice";
import { productRetailPrice } from "./productRetailPrice";
import { productWholesalePrice } from "./productWholesalePrice";
export var ProductDetailsHeader = function(param) {
    var product = param.product, selectedOption = param.selectedOption, perStyleOptionVariations = param.perStyleOptionVariations, badges = param.badges, hideBrandDetails = param.hideBrandDetails, brandReviewSummary = param.brandReviewSummary, isQuickAddModal = param.isQuickAddModal, brand = param.brand, centerTitle = param.centerTitle, productPromotionsState = param.productPromotionsState, brandPromotion = param.brandPromotion, showOutOfStockUnderProductName = param.showOutOfStockUnderProductName, surface = param.surface;
    var _badge_style;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var isOutOfStock = selectedOption !== undefined && !isProductOptionInStock(selectedOption, perStyleOptionVariations);
    var wholesalePrice = productWholesalePrice(strictLocalize, product, false, selectedOption, perStyleOptionVariations);
    var retailPrice = productRetailPrice(selectedOption);
    var originalPrice = originalPriceMoney(selectedOption);
    var badge = selectBadge(badges !== null && badges !== void 0 ? badges : [], "TOP");
    var isUK = useRetailerStore([
        "isUK"
    ]).isUK;
    var assignUpdateSaleColorCopy = useAssignSettingUpdateSaleColorCopy();
    var isUpdateSaleColorCopy = useSettingUpdateSaleColorCopy();
    useEffect(function() {
        if (badge) {
            assignUpdateSaleColorCopy();
        }
    }, [
        assignUpdateSaleColorCopy,
        badge
    ]);
    var shouldShowProductPromotions = showProductPromotions(selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.wholesale_promo_price) && !isOutOfStock;
    var isSingleOption = product.options.length === 1;
    var isConsumerMode = Storage.session.getItem(ENABLE_CONSUMER_MODE) === "true" && !isLoggedInRetailerOrBrandPreview();
    return /*#__PURE__*/ _jsxs(Wrapper, {
        $isQuickAddModal: isQuickAddModal,
        $isSingleOption: isSingleOption,
        "data-test-id": "product-detail-header",
        children: [
            hideBrandDetails ? null : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(MaybeSimpleBrandTitle, {
                        brand: brand,
                        product: product,
                        brandReviewSummary: brandReviewSummary
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: "3x"
                    })
                ]
            }),
            badge ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraphSansMedium",
                        color: isUpdateSaleColorCopy ? Core.text.default : Decorative.shade.orange,
                        children: (_badge_style = badge.style) === null || _badge_style === void 0 ? void 0 : _badge_style.badge_message
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        size: 4
                    })
                ]
            }) : null,
            /*#__PURE__*/ _jsx(ProductName, {
                brand: brand,
                isQuickAddModal: isQuickAddModal,
                centerTitle: centerTitle,
                product: product
            }),
            isLoggedInRetailerOrBrandPreview() ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(RedesignedOutOfStockText, {
                        isOutOfStock: isOutOfStock,
                        showOutOfStock: showOutOfStockUnderProductName,
                        dataTestId: "out-of-stock-text-in-Header"
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        size: "2x"
                    }),
                    /*#__PURE__*/ _jsx(Price, {
                        isUKRetailer: isUK,
                        showProductPromotions: shouldShowProductPromotions,
                        originalPrice: originalPrice,
                        wholesalePrice: wholesalePrice !== null && wholesalePrice !== void 0 ? wholesalePrice : "",
                        retailPrice: retailPrice,
                        isLoggedInOrBrandPreview: true,
                        isOutOfStock: isOutOfStock,
                        showOutOfStockUnderProductName: showOutOfStockUnderProductName,
                        priceTextSurface: surface !== null && surface !== void 0 ? surface : PRICE_TEXT_SURFACE.PDP
                    }),
                    /*#__PURE__*/ _jsx(ProductDetailsPromotion, {
                        brandPromotion: brandPromotion,
                        brand: brand,
                        productPromotionsState: productPromotionsState,
                        product: product
                    })
                ]
            }) : /*#__PURE__*/ _jsx(LoggedOutPrice, {
                retailPrice: retailPrice,
                showWSP: !isConsumerMode
            })
        ]
    });
};
// eslint-disable-next-line @faire/prefer-slate-attrs -- move align to a prop
var Wrapper = styled(Column).withConfig({
    componentId: "sc-19fcfc01-0"
})([
    "align-items:",
    ";margin:0;width:100%;"
], function(props) {
    if (props.$isQuickAddModal) {
        return props.$isSingleOption ? "center" : "stretch";
    } else {
        return "flex-start";
    }
});
